import { CdkDragDrop, CdkDragMove, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, NgModule, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { DialogService } from '../../../../../../core/services/dialog.service';
import { HelperService } from '../../../../../../core/services/helper.service';
import { slideInTopAnimation } from '../../../../../../utils/animations';
import { AiTakeOffComponent } from '../../../../../takeoff/ai-take-off/ai-take-off.component';
import { EstimatePlansTakeOffsStatesV2, EstimatePlansTakeOffsStoresV2Service } from '../../feat-estimate-plans-take-offs-v2/domain';
import { EstimatePlansV2StoresService } from '../../plans-widget/feat-estimate-plans-v2/domain';
import { EstimateMeasurementCategoryModule } from '../ui-estimate-measurement-category/estimate-measurement-category.component';
import { EstimateMeasurementSearchComponent } from '../ui-estimate-measurement-search/estimate-measurement-search.component';
import { EstimateMeasurementModule } from '../ui-estimate-measurement/estimate-measurement.component';
import { EstimateMeasurementsSettingsModule } from '../ui-estimate-measurements-settings/estimate-measurements-settings.component';
import {
    EstimateDragItemTypes,
    EstimateMeasurementCategoryVM,
    EstimateMeasurementDrawingStatus,
    EstimateMeasurementPrintModes,
    EstimateMeasurementStoresService,
    EstimateMeasurementViewSettings,
    EstimateMeasurementVM,
    slideInTopParams,
    TakeOffSupportedUnit,
} from './domain';
import { EstimateMeasurementAutoSaveService } from './estimate-measurement-auto-save.service';
import { EstimateMeasurementCalculatorService } from './estimate-measurement-calculator.service';
import { EstimateMeasurementCategoryService } from './estimate-measurement-category.service';
import { EstimateMeasurementTakeOffsService } from './estimate-measurement-take-offs.service';
import { EstimateMeasurementsDragDropService } from './estimate-measurements-drag-drop.service';
import { EstimateMeasurementsPrintingService } from './estimate-measurements-printing.service';
import { EstimateMeasurementsService } from './estimate-measurements.service';

@Component({
    selector: 'app-estimate-measurements-v2',
    templateUrl: './estimate-measurements-v2.component.html',
    styleUrls: ['./estimate-measurements-v2.component.scss'],
    animations: [slideInTopAnimation],
    standalone: false,
})
export class EstimateMeasurementsV2Component implements OnInit, AfterViewInit, OnDestroy {
    // @ts-ignore TS2564
    @Input() viewSettings: EstimateMeasurementViewSettings;

    // @ts-ignore TS2564
    public states$: Observable<EstimatePlansTakeOffsStatesV2>;
    // @ts-ignore TS2564
    public hasScaledPlans$: Observable<boolean>;
    // @ts-ignore TS2564
    public measurementCategories$: Observable<EstimateMeasurementCategoryVM[]>;
    // @ts-ignore TS2564
    public supportedUnits$: Observable<TakeOffSupportedUnit[]>; // TODO:: remove when lg flag 'measurement-uom-setting' is deleted
    // @ts-ignore TS2564
    public supportedUnits: TakeOffSupportedUnit[];

    // @ts-ignore TS2564
    public measurementsDrawingStatus$: Observable<EstimateMeasurementDrawingStatus>;

    public isExpanded = true;
    public isDragDisabled = true;

    public readonly slideInTopParams = slideInTopParams['measurement-list'];
    public readonly dragItemTypes = EstimateDragItemTypes;

    public hasPlans = toSignal(this.estimatePlansV2StoresService.estimatePlans$.pipe(map((plans) => plans.length > 0)));

    @ViewChild(NgScrollbar, { static: false }) private perfectScrollbarDirectiveRef?: NgScrollbar;

    constructor(
        private readonly estimateMeasurementCategoryService: EstimateMeasurementCategoryService,
        private readonly estimateMeasurementsService: EstimateMeasurementsService,
        private readonly estimateMeasurementCalculatorService: EstimateMeasurementCalculatorService,
        private readonly estimateMeasurementTakeOffsService: EstimateMeasurementTakeOffsService,
        private readonly estimateMeasurementsPrintingService: EstimateMeasurementsPrintingService,
        private readonly estimateMeasurementAutoSaveService: EstimateMeasurementAutoSaveService,
        private readonly estimateMeasurementsDragDropService: EstimateMeasurementsDragDropService,
        private readonly estimateMeasurementStoresService: EstimateMeasurementStoresService,
        private readonly estimatePlansV2StoresService: EstimatePlansV2StoresService,
        private readonly estimatePlansTakeOffsStoresV2Service: EstimatePlansTakeOffsStoresV2Service,
        private readonly dialogService: DialogService,
        private readonly helperService: HelperService
    ) {}

    ngOnInit(): void {
        this.hasScaledPlans$ = this.estimatePlansV2StoresService.estimatePlans$.pipe(
            map((plans) => plans.filter((p) => p.isScaled).length > 0)
        );
        this.states$ = this.estimatePlansTakeOffsStoresV2Service.estimatePlansTakeOffsStates$;
        this.measurementCategories$ = this.estimateMeasurementStoresService.measurementCategories$;
        this.supportedUnits = this.estimateMeasurementTakeOffsService.supportedUnits;

        this.measurementsDrawingStatus$ = this.estimateMeasurementStoresService.measurementsDrawingStatus$;

        if (!this.viewSettings.isOverLayer) {
            this.estimateMeasurementAutoSaveService.startAutoSave();
        } else {
            this.estimateMeasurementAutoSaveService.completeAutoSave();
        }
    }

    ngAfterViewInit(): void {
        if (this.viewSettings.isOverLayer && this.estimateMeasurementsDragDropService.currentScrollY) {
            this.perfectScrollbarDirectiveRef?.update();
            this.perfectScrollbarDirectiveRef?.scrollTo({ top: this.estimateMeasurementsDragDropService.currentScrollY });
        }
    }

    ngOnDestroy(): void {
        if (!this.viewSettings.isOverLayer) {
            this.estimateMeasurementAutoSaveService.completeAutoSave();
        } else {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
            // enable auto save for take off measurement screen
            this.estimateMeasurementAutoSaveService.startAutoSave();
        }
    }

    //#region Category
    public onAddCategory() {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementCategoryService.generateMeasurementCategory();
    }

    public onUpdateCategory(measurementCategory: EstimateMeasurementVM) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        if (!measurementCategory.originalMeasurement) {
            this.estimateMeasurementCategoryService.createMeasurementCategory(measurementCategory);
        } else {
            this.estimateMeasurementCategoryService.updateMeasurementCategory(measurementCategory);

            if (this.viewSettings.isOverLayer) {
                this.estimateMeasurementAutoSaveService.autoSave();
            }
        }
    }

    public onDeleteCategory(measurementCategory: EstimateMeasurementVM) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        if (!measurementCategory.originalMeasurement) {
            this.deleteMeasurementCategory(measurementCategory);
            return;
        }

        const params = {
            title: 'Are you sure you want to delete?',
            text: 'This will delete the selected category and all the related measurements.',
        };
        this.dialogService.show(params).then(
            () => {
                this.deleteMeasurementCategory(measurementCategory);
            },
            () => ({})
        );
    }

    private deleteMeasurementCategory(measurementCategory: EstimateMeasurementVM) {
        this.estimateMeasurementCategoryService
            .deleteMeasurementCategory(measurementCategory, this.viewSettings.isOverLayer)
            .pipe(
                filter((isDeleted: boolean) => !!isDeleted && this.viewSettings.isOverLayer),
                take(1)
            )
            .subscribe(() => this.estimateMeasurementAutoSaveService.autoSave());
    }

    public deleteAllMeasurements() {
        const params = {
            title: 'Are you sure you want to delete all measurements?',
            text: 'This will delete all your measurements and drawings.',
            okText: 'Delete all',
        };
        this.dialogService.show(params).then(
            () => {
                this.estimateMeasurementCategoryService.deleteAllMeasurements().subscribe();
            },
            () => ({})
        );
    }

    public onToggleCategory(measurementCategory: EstimateMeasurementVM) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementCategoryService.updateMeasurementCategory(measurementCategory);
    }

    public onDragCategoryMoved(event: CdkDragMove<EstimateMeasurementVM>, isCategory: boolean) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementsDragDropService.isDraging = true;

        this.estimateMeasurementsDragDropService.updateItemPosition(
            event.pointerPosition.x,
            event.pointerPosition.y,
            isCategory ? EstimateDragItemTypes.Category : EstimateDragItemTypes.Measurement,
            event.delta.y
        );
    }

    public onDropMeasurementCategory(event: CdkDragDrop<EstimateMeasurementVM[]>) {
        this.estimateMeasurementCategoryService.moveItem(
            // @ts-ignore TS2345
            event.container.data[event.currentIndex],
            this.estimateMeasurementsDragDropService.dragTargetInfo
        );

        this.estimateMeasurementsDragDropService.isDraging = false;

        if (this.viewSettings.isOverLayer) {
            this.estimateMeasurementAutoSaveService.autoSave();
        }
    }
    //#endregion Category

    //#region Measurement
    public onAddMeasurement(categoryId: string) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementsService.generateMeasurement(categoryId);
    }

    public onMeasurementUpdated(newMeasurement: EstimateMeasurementVM) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        if (!newMeasurement.originalMeasurement) {
            if (this.viewSettings.isOverLayer) {
                this.estimateMeasurementAutoSaveService.autoSave();
            }
            this.estimateMeasurementsService.createMeasurement(newMeasurement);
        } else {
            this.estimateMeasurementsService.updateMeasurement(newMeasurement);
            if (this.viewSettings.isOverLayer) {
                this.estimateMeasurementAutoSaveService.autoSave();
            }
        }
    }

    public onDelete(measurement: EstimateMeasurementVM) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        if (!measurement.parentId) {
            this.onDeleteCategory(measurement);
            return;
        }

        if (!measurement.originalMeasurement) {
            this.deleteMeasurement(measurement);
            return;
        }

        const params = {
            title: 'Delete Measurement?',
            text: "If you go ahead, you won't be able to get it back.",
        };
        this.dialogService.show(params).then(
            () => {
                this.deleteMeasurement(measurement);
            },
            () => ({})
        );
    }

    private deleteMeasurement(measurement: EstimateMeasurementVM) {
        this.estimateMeasurementsService
            .deleteMeasurement(measurement, this.viewSettings.isOverLayer)
            .pipe(
                filter((isDeleted: boolean) => !!isDeleted && this.viewSettings.isOverLayer),
                take(1)
            )
            .subscribe(() => {
                this.estimateMeasurementAutoSaveService.autoSave();
            });
    }

    public onCalculate(measurement: EstimateMeasurementVM) {
        this.estimateMeasurementsService.updateMeasurement(measurement);
        this.estimateMeasurementCalculatorService.calculate(measurement);
    }

    public onTakeOffs(measurement: EstimateMeasurementVM) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementsService.updateMeasurement(measurement);

        let isCompleteAutoSave = this.viewSettings.isOverLayer;
        if (!this.viewSettings.isOverLayer) {
            isCompleteAutoSave = this.estimateMeasurementTakeOffsService.doTakeOffs();
        }

        if (isCompleteAutoSave) {
            this.estimateMeasurementAutoSaveService.autoSave();
            this.estimateMeasurementsDragDropService.currentScrollY = this.perfectScrollbarDirectiveRef?.nativeElement.scrollTop;
        }
    }

    public launchTakeoffs() {
        const initialState = { measurement: null, showMeasurementOverlayer: true };
        this.helperService.showModal(AiTakeOffComponent, { class: 'bx-modal-fullscreen', initialState });
    }

    public toggleTakeOffsForMeasurement(measurement: EstimateMeasurementVM) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementsService.updateMeasurement(measurement);

        if (this.viewSettings.isOverLayer) {
            this.estimateMeasurementAutoSaveService.autoSave();
        }
    }

    public onDragMeasurementMoved(event: CdkDragMove<EstimateMeasurementVM>) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementsDragDropService.isDraging = true;

        this.estimateMeasurementsDragDropService.updateItemPosition(
            event.pointerPosition.x,
            event.pointerPosition.y,
            EstimateDragItemTypes.Measurement,
            event.delta.y
        );
    }

    public onDropMeasurement(event: CdkDragDrop<EstimateMeasurementVM[]>) {
        this.estimateMeasurementCategoryService.moveItem(
            // @ts-ignore TS2345
            event.container.data[event.currentIndex],
            this.estimateMeasurementsDragDropService.dragTargetInfo
        );

        this.estimateMeasurementsDragDropService.isDraging = false;
        if (this.viewSettings.isOverLayer) {
            this.estimateMeasurementAutoSaveService.autoSave();
        }
    }
    //#endregion Measurement

    //#region Others
    public toggleMeasurements() {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.isExpanded = !this.isExpanded;
    }

    public onPrintMeasurements(mode: EstimateMeasurementPrintModes) {
        this.estimateMeasurementsPrintingService.printMeasurements(mode);
    }

    public onEmailMeasurements(mode: EstimateMeasurementPrintModes) {
        this.estimateMeasurementsPrintingService.emailMeasurements(mode);
    }

    public toggleAllTakeOffs(toggle: boolean) {
        if (this.viewSettings.isOverLayer) {
            // Update current measurement for take offs before move to another measurement
            this.estimateMeasurementTakeOffsService.updateTakeoffsForMeasurementInSlowMode();
        }

        this.estimateMeasurementTakeOffsService.toggleAllDrawings(toggle);
    }

    public onToggleDragging(isDragEnabled: boolean) {
        this.isDragDisabled = !isDragEnabled;
    }

    public onSearchFilterChanged(searchTerm: string) {
        this.estimateMeasurementStoresService.searchFilter$.next(searchTerm);
    }
    //#endregion Others
}

const uiModules = [EstimateMeasurementsSettingsModule, EstimateMeasurementCategoryModule, EstimateMeasurementModule];
@NgModule({
    declarations: [EstimateMeasurementsV2Component],
    imports: [CommonModule, DragDropModule, NgScrollbarModule, EstimateMeasurementSearchComponent, ...uiModules],
    exports: [EstimateMeasurementsV2Component],
})
export class EstimateMeasurementsV2Module {}
